import { createSlice } from "@reduxjs/toolkit";
import { createAsyncThunk } from "@reduxjs/toolkit";
import SublyApi from "../helpers/Api";

/** Reason constants for ending impersonation sessions. */
export const IMPERSONATION_END_REASONS = {
	MANUAL: 'manual',
	EXPIRED: 'expired',
};

// Async thunk: stop impersonation and notify backend for audit logging
export const stopImpersonation = createAsyncThunk(
	"admin/stopImpersonation",
	async (reason = IMPERSONATION_END_REASONS.MANUAL, { getState }) => {
		const state = getState();
		const adminToken = state.admin.originalAdminToken || state.admin.token;
		const targetUsername = state.admin.impersonatedUser?.username;

		// Attempt audit log call — if it fails, the error propagates to the
		// .rejected handler, which still clears impersonation state.
		try {
			if (adminToken && targetUsername) {
				await SublyApi.endImpersonation({ targetUsername, reason }, adminToken);
			}
		} catch (error) {
			console.warn('stopImpersonation: audit log call failed', error);
			throw error;
		}

		return { reason };
	},
	{
		// Prevent concurrent dispatches (race between rehydration check and 401 interceptor)
		condition: (_reason, { getState }) => {
			const { admin } = getState();
			return admin.isImpersonating;
		},
	}
);

// First, create the thunk
export const adminSignup = createAsyncThunk(
	"admin/adminSignup",
	async (data, { rejectWithValue }) => {
		try {
			const response = await SublyApi.adminSignup(data);

			return response;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

// Thunk for admin login
export const adminLogin = createAsyncThunk(
	"admin/adminLogin",
	async (data, { rejectWithValue }) => {
		try {
			const response = await SublyApi.adminLogin(data);
			console.log(response);

			return response;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

// Thunk for getting admin
export const getAdmin = createAsyncThunk(
	"admin/getAdmin",
	async (data, { rejectWithValue }) => {
		try {
			const response = await SublyApi.getAdmin(data.username, data.token);

			return response;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

// Thunk for admin impersonation
export const startImpersonation = createAsyncThunk(
	"admin/startImpersonation",
	async (username, { getState, rejectWithValue }) => {
		try {
			const state = getState();
			const adminToken = state.admin.token;

			if (!adminToken) {
				throw new Error("Admin token not found");
			}

			const response = await SublyApi.impersonateUser(username, adminToken);

			return {
				...response,
				originalAdminToken: adminToken,
			};
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const initialState = {
	allAdmins: [],
	currentAdmin: { isSuper: false },
	token: null,
	loading: false,
	errors: [],
	success: false, // monitors the success of an api call, in case it needs to be passed to a component
	remember: true, // controls whether an admin's info is stored after dismount
	// Impersonation state
	isImpersonating: false,
	impersonatedUser: null,
	impersonatedToken: null,
	originalAdminToken: null,
	impersonationInfo: null,
};

export const adminSlice = createSlice({
	name: "admin",
	initialState: initialState,
	reducers: {
		adminLogout: (state, action) => {
			return initialState;
		},
		clearSuccess: (state, action) => {
			state.success = false;
		},
		clearErrors: (state, action) => {
			state.errors = [];
		},
		addToStorage: (state, action) => {
			state.currentAdmin = { ...state.currentAdmin, ...action.payload };
		},
		changeRemember: (state, action) => {
			state.remember = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(adminSignup.fulfilled, (state, action) => {
				state.currentAdmin = action.payload.admin;
				state.token = action.payload.token;
				state.errors = [];
			})
			.addCase(adminSignup.pending, (state, action) => {
				state.errors = [];
			})
			.addCase(adminSignup.rejected, (state, action) => {
				state.errors = action.payload;
			})
			.addCase(adminLogin.fulfilled, (state, action) => {
				state.currentAdmin = action.payload.admin;
				state.token = action.payload.token;
				state.errors = [];
			})
			.addCase(adminLogin.rejected, (state, action) => {
				state.errors = action.payload;
			})
			.addCase(adminLogin.pending, (state, action) => {
				state.errors = [];
			})
			.addCase(getAdmin.fulfilled, (state, action) => {
				state.currentAdmin = action.payload.admin;
				state.token = action.payload.token;
				state.errors = [];
			})
			.addCase(getAdmin.rejected, (state, action) => {
				state.errors = action.payload;
			})
			.addCase(startImpersonation.pending, (state, action) => {
				state.loading = true;
				state.errors = [];
			})
			.addCase(startImpersonation.fulfilled, (state, action) => {
				const { token, user, impersonationInfo, originalAdminToken } = action.payload;
				state.isImpersonating = true;
				state.impersonatedUser = user;
				state.impersonatedToken = token;
				state.originalAdminToken = originalAdminToken;
				state.impersonationInfo = impersonationInfo;
				state.loading = false;
				state.errors = [];
			})
			.addCase(startImpersonation.rejected, (state, action) => {
				state.loading = false;
				state.errors = action.payload;
			})
			// stopImpersonation async thunk - clear impersonation state
			.addCase(stopImpersonation.fulfilled, (state) => {
				state.isImpersonating = false;
				state.impersonatedUser = null;
				state.impersonatedToken = null;
				state.impersonationInfo = null;
				state.originalAdminToken = null;
			})
			.addCase(stopImpersonation.rejected, (state) => {
				// Still clear state even if API fails
				state.isImpersonating = false;
				state.impersonatedUser = null;
				state.impersonatedToken = null;
				state.impersonationInfo = null;
				state.originalAdminToken = null;
			});
	},
});

export const {
	adminLogout,
	clearSuccess,
	clearErrors,
	addToStorage,
	changeRemember,
} = adminSlice.actions;

export default adminSlice.reducer;
